import React, { PureComponent } from 'react';
import ERROR_PROMPT from '../constants/ErrorPrompt';

import { isRobot, getDeliveryType, r2h, argSubscribe, getUnitWp } from '../vodjs/lib';
import StandardSendScreen from '../screens/StandardSendScreen';
import ServiceUnavailableScreen from '../screens/ServiceUnavailableScreen';
import ConfirmTaskCancelScreen from '../screens/ConfirmTaskCancelScreen';

import { env } from '../vodjs/settings';
import { getCurrentView } from '../vodjs/TrackedTasks';
import TaskRequest from '../vodjs/TaskRequest';
import MoxiModalContext from './MoxiModalContext';
import { elasticLogTaskAction, elasticLogError } from '../vodjs/ElasticLog';
import { DRButton } from './DRControls';
import { isMobile, noteInteraction, arrLast} from '../vodjs/Utils';
import { noSelect, wpStyle, boxyShadow, panY } from '../constants/Styles';
import { TASK_STATUS } from '../constants/Tasks';
import { getUseFirestore } from '../constants/Meta';

const mobile = isMobile();


class QueueView extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loaderVisible: false,
      isLoading: false,
      cancelConfirmVisible: false,
    };

    // this.noPendingDeliveriesView = this.noPendingDeliveriesView.bind(this);
    this.queueListView = this.queueListView.bind(this);

    this.cancelTaskDetails = {
      onCancelConfirm: () => {},
      start: '',
      end: '',
    };
  }

  componentDidMount() {
    //re-render in case unit name changes
    this.unsubscribeArgTypes = argSubscribe((argTypes) => this.setState({ changeId: (this.state.changeId || 0) + 1 }));
  }

  componentWillUnmount() {
    if (this.unsubscribeArgTypes) {
      this.unsubscribeArgTypes();
      this.unsubscribeArgTypes = undefined;
    }
  }

  async cancelTask(task) {
    this.setState({ isLoading: true });

    const unit = getUnitWp();

    //if was load task for 1:M batch, gather info to cancel all batch tasks
    let allUuids = [task.uuid];
    let batch = task.task_data.batch;
    let fallbackAdds = [];
    if(batch?.batchType == 'oneToMany' && batch.index == 0) {
      let batchTasks = this.props.displayTasks.filter(({task:t}) => t.task_data?.batch?.id == batch.id);
      allUuids = batchTasks.map(({task:t}) => t.uuid);

      //strengthening in case of disruption to task subscription
      batch.uuids.forEach(uuid => {
        if(allUuids.indexOf(uuid) < 0) {
          allUuids.push(uuid);
          fallbackAdds.push(uuid);
        }
      });
    }
    let fallbackMsg = fallbackAdds.length ? `; fallbackAdds: ${fallbackAdds.map(uuid => uuid.slice(0, 6)).join(', ')}` : '';

    //cancel proximate task first
    allUuids = [task.uuid, ...allUuids.filter(uuid => uuid != task.uuid)];

    elasticLogTaskAction({
      what: 'taskCancelled',
      msg: `Canceling ${task.uuid} from kiosk/QueueView of ${unit}${fallbackMsg}`,
      taskUuid: task.uuid,
      restricted: !!task.task_data.restricted,
      mfa: !!task.task_data.code_required,
      context: 'queueView',
      unit,
      allUuids,
      batch,
      fallbackAdds
    });

    let errors = [];

    if(this.props.autoPrioConfig?.batchTaskCleanCancels) {
      let result = await TaskRequest.mbotCancelTasks([task.uuid]);
      if(result?.error)
        errors.push(result.error);
    }
    else {
      if(getUseFirestore()) {
        for(let uuid of allUuids) {
          try {
            let response = await TaskRequest.cancel(uuid);
            if(response?.error) 
              errors.push(`canceling ${uuid}: ${response.error}`);
          }
          catch(err) {
            errors.push(`canceling ${uuid}: ` + err);
          }
        }
      }
      else {
      let result = await TaskRequest.mbotCancelTasks(allUuids);
      if(result?.error)
        errors.push(result.error);
      }
    }

    this.setState({ isLoading: false });
    if (errors.length) {
      this.props.onError(ERROR_PROMPT.default);

      elasticLogError({
        what: 'taskCancelError',
        msg: errors.join('; '),
        taskUuid: task.uuid,
        context: 'queueView',
        unit,
        allUuids
      });
    }
  }

  /**
   * We don't need to show the robot name on robot UI. This is meant for the kiosks
   */
  renderRobotName = (task) => {
    if (task.claimed_by && !isRobot()) {
      return (<span style={{fontSize: mobile ? 16 : 30, marginLeft: 8 }}>by {task.claimed_by}</span>);
    }
    return null;
  }

  queueListView() {
    let regStyle = { fontWeight: 500, fontSize: mobile ? 16 : 30 };
    let boldStyle = { fontWeight: 800, fontSize: mobile ? 12 : 30 };
    let maxHeight = 'calc(100vh - 340px)'; //prevents overflow when multiple tasks
    const { showCancel } = this.props;
    const getTaskStyle = (task) => ({
      opacity: task.status === TASK_STATUS.CANCEL_REQUESTED ? '0.3' : 1,
      pointerEvents: task.status === TASK_STATUS.CANCEL_REQUESTED ? 'none' : 'auto',
      display: 'flex',
      flexDirection: 'row',
      padding: 7,
      ...panY,
    });

    return (
      <div style={{ flex: 1, justifyContent: 'center', alignItems: 'center', maxHeight, ...noSelect, ...panY }}>
        {this.props.displayTasks.map(({ task, inProgress }) => {
          if (!task) return; //caution
          let { canCancel, showNotes, restricted, estopped } = getCurrentView(task, this.props.robotMap);
          let note = task.task_data.sender_note;
          let { start_goal, end_goal } = task.task_level_args;
          let start = (start_goal || {}).value, end = (end_goal || {}).value;

          let batch = task.task_data.batch;
          if(start && batch?.batchType == 'oneToMany')
            start = batch.stops[0];
          if(end && batch?.batchType == 'manyToOne')
            end = arrLast(batch.stops);

          const delivLead = restricted
            ? task.task_data.code_required
              ? 'Multi-factor delivery'
              : 'Restricted delivery'
            : 'Delivery';

          let deliveryInfo;
          if (start && end) {
            deliveryInfo = (
              <div style={inProgress ? boldStyle : regStyle}>
                {delivLead} from <span style={wpStyle}>{r2h(start)}</span> to <span style={wpStyle}>{r2h(end)}</span>
                {this.renderRobotName(task)}
              </div>
            );
          }
          else if (start) {
            deliveryInfo = (
              <div style={inProgress ? boldStyle : regStyle}>
                {delivLead} from <span style={wpStyle}>{r2h(start)}</span>
                {this.renderRobotName(task)}
              </div>
            );
          }
          else if (end) {
            deliveryInfo = (
              <div style={inProgress ? boldStyle : regStyle}>
                {delivLead} to <span style={wpStyle}>{r2h(end)}</span>
                {this.renderRobotName(task)}
              </div>
            );
          }
          else {
            deliveryInfo = <div style={inProgress ? boldStyle : regStyle}>Delivery</div>;
          }

          return (
            <div key={task.uuid}>
              <div style={getTaskStyle(task)}>
                <div style={{ flexDirection: 'column', flex: 1 }}>
                  {deliveryInfo}
                  {showNotes && !!note && (
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{task.task_data.sender_note}</div>
                  )}
                  {canCancel && estopped && (
                    <div style={{ color: 'red', textAlign: 'center', fontWeight: 'bold' }}>
                      Moxi is currently e-stopped
                    </div>
                  )}
                </div>

                {canCancel && showCancel && (
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      marginRight: 24,
                      cursor: 'pointer',
                      display: 'flex',
                      flexFlow: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={() => {
                      noteInteraction();
                      this.props.primeAudio();
                      this.cancelTaskDetails = {
                        start: start,
                        end: end,
                        onCancelConfirm: () => {
                          this.setState({ cancelConfirmVisible: false });
                          this.cancelTask(task);
                        },
                      };
                      this.setState({ cancelConfirmVisible: true });
                    }}
                  >
                    <img
                      draggable="false"
                      style={{ width: '32px', height: '32px' }}
                      src={require('../assets/images/close-x.png')}
                    />
                  </div>
                )}

                {!canCancel && <div style={{ width: '24px', height: '24px' }}></div>}
              </div>
              {task.status === TASK_STATUS.CANCEL_REQUESTED && (
                <div style={{ textAlign: 'right', marginRight: 10, color: '#2e2e2e' }}>
                  This task is being cancelled
                </div>
              )}
              <hr style={{ marginLeft: 10, marginRight: 10 }} />
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    let buttonFontSize = 18;
    let buttonHeight = 42;
    let buttonWidth = '45%';
    const { showRequestMoxi } = this.props;

    let confirmAction = () => {
      this.props.primeAudio(); //prime audio in case needed
      // if (env.siteConfig.checkServiceAvailabilityNow(getDeliveryType())) {
        return this.props.presentModal(StandardSendScreen);
      // }
      // else {
      //   return this.props.presentModal(ServiceUnavailableScreen);
      // }
    };

    return (
      <div style={{ flex: 1, ...panY }}>
        <div
          style={{ height: '100%', width: '100%', display: 'flex', flexFlow: 'column', position: 'relative', ...panY }}
        >
          <div
            style={{
              display: 'flex',
              flex: '1 1 70%',
              borderRadius: '8px',
              ...boxyShadow,
              overflowY: 'scroll',
              overflowX: 'hidden',
              ...panY,
            }}
          >
            {this.state.cancelConfirmVisible && (
              <ConfirmTaskCancelScreen
                startUnit={r2h(this.cancelTaskDetails.start)}
                endUnit={r2h(this.cancelTaskDetails.end)}
                onConfirm={this.cancelTaskDetails.onCancelConfirm}
                onClose={() => this.setState({ cancelConfirmVisible: false })}
                modalVisible={this.state.cancelConfirmVisible}
              />
            )}
            {!this.state.cancelConfirmVisible && this.queueListView()}
          </div>

          {!isRobot() && !this.state.cancelConfirmVisible && showRequestMoxi && (
            <div
              id="request_moxi"
              style={{
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                paddingTop: 10,
              }}
            >
              <DRButton
                customizeStyle={{
                  width: '12em',
                  height: '2em',
                  fontSize: 30,
                  color: 'white',
                  backgroundColor: '#47b7b9',
                  fontWeight: 'bold',
                }}
                phrase={'Request Moxi'}
                borderColor={'#47b7b9'}
                action={confirmAction}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

QueueView.contextType = MoxiModalContext;
export default QueueView;
